// Single page that hosts the Stammdaten sub-views (Kunden, Tätigkeiten,
// Tags) as tabs, so the sidebar only needs one entry. The underlying
// sub-pages still live at /customers /services /tags and remain valid
// deep-links — this page just embeds them as tabs. Projekte was promoted
// out into its own sidebar entry under Projektmanagement.
import { createRoute, Link, Navigate } from '@tanstack/react-router'
import { Box, Layers, Package, Tag as TagIcon, Users2 } from 'lucide-react'
import type { ComponentType } from 'react'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { PageHeader } from '../components/ui/page-header'
import { TabNav, tabPillClass } from '../components/ui/tab-nav'
import { Route as rootRoute } from './__root'
import { CustomersPage } from './customers'
import { InventoryPage } from './inventory'
import { ProductsPage } from './products'
import { ServicesPage } from './services'
import { TagsPage } from './tags'

type TabId = 'customers' | 'services' | 'products' | 'tags' | 'inventory'

const TABS: {
  id: TabId
  label: string
  icon: typeof Users2
  component: ComponentType<{ embedded?: boolean }>
}[] = [
  { id: 'customers', label: 'Kunden', icon: Users2, component: CustomersPage },
  { id: 'services', label: 'Tätigkeiten', icon: Layers, component: ServicesPage },
  { id: 'products', label: 'Produkte', icon: Package, component: ProductsPage },
  { id: 'tags', label: 'Tags', icon: TagIcon, component: TagsPage },
  { id: 'inventory', label: 'Inventar', icon: Box, component: InventoryPage },
]

function isTabId(v: unknown): v is TabId {
  return (
    v === 'customers' || v === 'services' || v === 'products' || v === 'tags' || v === 'inventory'
  )
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stammdaten',
  validateSearch: (s: Record<string, unknown>): { tab: TabId } => ({
    tab: isTabId(s.tab) ? s.tab : 'customers',
  }),
  component: StammdatenPage,
})

function StammdatenPage() {
  const { tab } = Route.useSearch()
  // Mobile-Shell (<640): Host-Titel; die Tab-Zeile bleibt im Content, der
  // kontextabhängige ＋ (+ Suche) kommt aus der eingebetteten Tab-Seite
  // selbst (deren MobileBottomSlots portalen auch hier — bestehende
  // Handler je Tab, Mapping §3).
  useMobilePageTitle('Stammdaten')
  // Old deep-link to the projects tab: bounce to its own page so existing
  // bookmarks keep working after the promotion to a top-level entry.
  if (typeof window !== 'undefined') {
    const raw = new URL(window.location.href).searchParams.get('tab')
    if (raw === 'projects') return <Navigate to="/projects" />
  }
  const Active = TABS.find((t) => t.id === tab)?.component ?? CustomersPage

  return (
    <div className="space-y-6">
      {/* Einreihiger Standard-Kopf (Paket C): Tabs als Pills im Kopf statt
          eigener Underline-Zeile — keep-row, weil die Tabs am Phone die
          einzige Bereichs-Navigation sind (eingebettete Seiten liefern
          nur +/Suche). */}
      <PageHeader
        title="Stammdaten"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Stammdaten-Bereiche">
            {TABS.map((t) => {
              const active = t.id === tab
              const Icon = t.icon
              return (
                <Link
                  key={t.id}
                  to="/stammdaten"
                  search={{ tab: t.id }}
                  className={tabPillClass(active)}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={active ? 2.25 : 2} />
                  {t.label}
                </Link>
              )
            })}
          </TabNav>
        }
      />

      <Active embedded />
    </div>
  )
}
